import Head from "next/head";
import SectionHeader from "@/components/SectionHeader";

/**
 * Reusable FAQ Section Component with Schema.org FAQPage markup
 * @param {Array} items - FAQ items with { question, answer } structure
 * @param {String} title - Section title
 * @param {String} subtitle - Optional subtitle
 * @param {String} id - Optional section ID
 * @param {Number} dataScrollOffset - Optional scroll offset
 * @param {String} className - Optional additional classes
 */
export default function FaqSection({
  items = [],
  title,
  subtitle,
  id,
  dataScrollOffset = 0,
  className = "",
}) {
  // Default items for homepage if none provided
  const faqItems =
    items.length > 0
      ? items
      : [
          {
            question: "Wie viel kostet eine Fotobox?",
            answer:
              "Die Preise hängen von Ausstattung, Mietdauer, Druckoptionen und Serviceumfang ab. Versandmodelle sind meist günstiger als Komplettlösungen mit Vor-Ort-Service.",
          },
          {
            question: "Lohnt sich eine Druck-Flatrate?",
            answer:
              "Bei größeren Events mit vielen Gästen kann eine Flatrate sinnvoll sein. Bei kleineren Feiern reicht oft ein Druckkontingent.",
          },
          {
            question: "Brauche ich Betreuung vor Ort?",
            answer:
              "Für private Feiern ist das selten notwendig. Bei Firmenveranstaltungen oder großen Events kann ein Ansprechpartner vor Ort sinnvoll sein.",
          },
          {
            question: "Wie lange im Voraus sollte ich buchen?",
            answer:
              "Beliebte Termine – besonders in der Hochzeitssaison – sind früh ausgebucht. Eine frühzeitige Anfrage erhöht die Auswahlmöglichkeiten.",
          },
        ];

  const sectionTitle = title || "Häufige Fragen zur Fotobox-Miete";

  // Schema.org FAQPage markup
  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faqItems.map((item) => ({
      "@type": "Question",
      name: item.question,
      acceptedAnswer: {
        "@type": "Answer",
        text: item.answer,
      },
    })),
  };

  return (
    <>
      {/* Schema.org FAQPage JSON-LD */}
      <Head>
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }}
        />
      </Head>

      {/* FAQ Section */}
      <section
        id={id}
        data-scroll-offset={dataScrollOffset}
        className={className}
      >
        <SectionHeader title={sectionTitle} subtitle={subtitle}>
          <div className="mt-10 space-y-6">
            {faqItems.map((item, index) => (
              <div
                key={index}
                className="border-b border-slate-200 pb-6 last:border-b-0 last:pb-0"
              >
                <h3 className="text-lg font-semibold text-slate-900">
                  {item.question}
                </h3>
                <p className="mt-2 text-slate-900">{item.answer}</p>
              </div>
            ))}
          </div>
        </SectionHeader>
      </section>
    </>
  );
}
